const STORAGE_KEY = "kraya_receipt_printer_settings_v1";

export const DEFAULT_PRINTER_SETTINGS = Object.freeze({
  mode: "browser",
  paperWidth: 58,
  baudRate: 9600,
  autoPrint: false,
  autoCut: false,
  feedLines: 4,
});

let activePort = null;

const numberInRange = (value, fallback, min, max) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
};

export const normalizePrinterSettings = (value = {}) => ({
  mode: value.mode === "serial" ? "serial" : "browser",
  paperWidth: Number(value.paperWidth) === 80 ? 80 : 58,
  baudRate: numberInRange(value.baudRate, 9600, 300, 2000000),
  autoPrint: value.autoPrint === true,
  autoCut: value.autoCut === true,
  feedLines: numberInRange(value.feedLines, 4, 1, 10),
});

export const loadPrinterSettings = () => {
  if (typeof window === "undefined") return { ...DEFAULT_PRINTER_SETTINGS };
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
    return normalizePrinterSettings({ ...DEFAULT_PRINTER_SETTINGS, ...saved });
  } catch {
    return { ...DEFAULT_PRINTER_SETTINGS };
  }
};

export const savePrinterSettings = (settings) => {
  const normalized = normalizePrinterSettings(settings);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

export const isWebSerialSupported = () => (
  typeof navigator !== "undefined" && "serial" in navigator
);

const portIsOpen = (port) => Boolean(port?.readable || port?.writable);

const describePort = (port) => {
  if (!port) return "Printer belum terhubung";
  const info = typeof port.getInfo === "function" ? port.getInfo() : {};
  if (info?.bluetoothServiceClassId) return "Bluetooth ESC/POS (SPP)";
  if (info?.usbVendorId) {
    const vendor = Number(info.usbVendorId).toString(16).padStart(4, "0");
    const product = Number(info.usbProductId || 0).toString(16).padStart(4, "0");
    return `USB Serial ${vendor}:${product}`;
  }
  return "Serial ESC/POS";
};

const openPort = async (port, settings) => {
  if (!port) throw new Error("Port printer tidak tersedia");
  if (!portIsOpen(port)) {
    await port.open({
      baudRate: Number(settings.baudRate || 9600),
      dataBits: 8,
      stopBits: 1,
      parity: "none",
      flowControl: "none",
      bufferSize: 1024,
    });
  }
  activePort = port;
  return { port, label: describePort(port) };
};

export const connectReceiptPrinter = async (settings, { request = true } = {}) => {
  if (!isWebSerialSupported()) {
    throw new Error("Browser ini belum mendukung Web Serial. Gunakan Chrome/Edge terbaru.");
  }

  if (activePort && portIsOpen(activePort)) {
    return { port: activePort, label: describePort(activePort) };
  }

  const granted = await navigator.serial.getPorts();
  if (!request && granted.length === 0) {
    throw new Error("Printer belum pernah diizinkan pada browser ini");
  }

  const port = request ? await navigator.serial.requestPort() : granted[0];
  return openPort(port, normalizePrinterSettings(settings));
};

export const disconnectReceiptPrinter = async () => {
  if (!activePort) return;
  const port = activePort;
  activePort = null;
  try {
    if (port.readable) {
      const reader = port.readable.locked ? null : port.readable.getReader();
      if (reader) {
        await reader.cancel().catch(() => {});
        reader.releaseLock();
      }
    }
    if (port.writable?.locked) return;
    await port.close();
  } catch {
    // The device may already be powered off or out of range.
  }
};

export const getReceiptPrinterStatus = () => ({
  connected: Boolean(activePort && portIsOpen(activePort)),
  label: activePort ? describePort(activePort) : "Printer belum terhubung",
});

const ascii = (value) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[–—]/g, "-")
  .replace(/[“”]/g, '"')
  .replace(/[‘’]/g, "'")
  .replace(/[^\x20-\x7E\n]/g, "?");

const money = (value) => Math.round(Number(value || 0)).toLocaleString("id-ID");

const wrapText = (value, width) => {
  const text = ascii(value).trim();
  if (!text) return [""];
  const result = [];
  let rest = text;
  while (rest.length > width) {
    let cut = rest.lastIndexOf(" ", width);
    if (cut < Math.floor(width * 0.5)) cut = width;
    result.push(rest.slice(0, cut).trimEnd());
    rest = rest.slice(cut).trimStart();
  }
  result.push(rest);
  return result;
};

const center = (value, width) => {
  const text = ascii(value).slice(0, width);
  const left = Math.max(0, Math.floor((width - text.length) / 2));
  return `${" ".repeat(left)}${text}`;
};

const columns = (left, right, width) => {
  const lhs = ascii(left);
  const rhs = ascii(right);
  const available = Math.max(1, width - rhs.length - 1);
  const leftLine = lhs.length > available ? lhs.slice(0, available) : lhs;
  return `${leftLine}${" ".repeat(Math.max(1, width - leftLine.length - rhs.length))}${rhs}`.slice(0, width);
};

const separator = (width, character = "-") => character.repeat(width);

const receiptLines = ({ sale, outlet, settings, paymentLabel, qrisDetails }) => {
  const width = settings.paperWidth === 80 ? 42 : 32;
  const createdAt = new Date(sale?.created_at || Date.now());
  const itemCount = (sale?.items || []).reduce((sum, item) => sum + Number(item.qty || 0), 0);
  const taxableBase = Math.max(0, Number(sale?.subtotal || 0) - Number(sale?.discount || 0));
  const taxRate = sale?.tax_rate || (taxableBase > 0 && Number(sale?.tax || 0) > 0
    ? Number(((Number(sale.tax) / taxableBase) * 100).toFixed(2))
    : 0);
  const label = paymentLabel || (sale?.payment_method === "qris" ? "QRIS" : "TUNAI");
  const lines = [];

  wrapText(String(outlet?.name || "OUTLET").toUpperCase(), width).forEach((line) => lines.push(center(line, width)));
  if (outlet?.address) wrapText(outlet.address, width).forEach((line) => lines.push(center(line, width)));
  if (outlet?.phone) lines.push(center(`Telp. ${outlet.phone}`, width));
  lines.push(center("BUKTI PEMBAYARAN", width));
  lines.push(separator(width, "="));
  lines.push(columns("No. Struk", sale?.invoice_no || "-", width));
  lines.push(columns("Tanggal", createdAt.toLocaleDateString("id-ID"), width));
  lines.push(columns("Jam", createdAt.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" }), width));
  lines.push(columns("Kasir", String(sale?.cashier_name || "-").toUpperCase(), width));
  lines.push(columns("Bayar", label, width));
  lines.push(separator(width));

  (sale?.items || []).forEach((item) => {
    wrapText(String(item.name || "PRODUK").toUpperCase(), width).forEach((line) => lines.push(line));
    lines.push(columns(`${item.qty} x ${money(item.price)}`, money(item.subtotal), width));
  });

  lines.push(separator(width));
  lines.push(columns("Subtotal", money(sale?.subtotal), width));
  if (Number(sale?.discount || 0) > 0) lines.push(columns("Diskon / Hemat", `-${money(sale.discount)}`, width));
  if (Number(sale?.tax || 0) > 0) lines.push(columns(`PPN${taxRate ? ` (${taxRate}%)` : ""}`, money(sale.tax), width));
  lines.push(columns("TOTAL", `Rp ${money(sale?.total)}`, width));
  lines.push(columns(label, money(sale?.cash_received || sale?.total), width));
  if (Number(sale?.change || 0) > 0) lines.push(columns("Kembalian", money(sale.change), width));
  lines.push(separator(width));
  lines.push(columns("Jumlah item", itemCount, width));

  if (sale?.payment_method === "qris") {
    lines.push(columns("Ref. QRIS", sale?.qris?.reference_no || "-", width));
    wrapText(`Customer: ${qrisDetails?.customerName || "-"}`, width).forEach((line) => lines.push(line));
    lines.push(columns("Approval", qrisDetails?.approvalCode || "-", width));
  }

  lines.push(separator(width, "="));
  lines.push(center("TERIMA KASIH", width));
  lines.push(center("Simpan struk sebagai bukti bayar", width));
  return lines;
};

const concatBytes = (...parts) => {
  const normalized = parts.map((part) => part instanceof Uint8Array ? part : Uint8Array.from(part));
  const total = normalized.reduce((sum, part) => sum + part.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  normalized.forEach((part) => {
    result.set(part, offset);
    offset += part.length;
  });
  return result;
};

const buildEscPosBytes = (lines, settings) => {
  const encoder = new TextEncoder();
  const text = encoder.encode(`${lines.join("\n")}\n`);
  const feed = encoder.encode("\n".repeat(Number(settings.feedLines || 4)));
  const commands = [
    [0x1b, 0x40],       // Initialize printer
    [0x1b, 0x74, 0x00], // Code page 0 / ASCII-compatible
    [0x1b, 0x61, 0x00], // Left alignment; text is preformatted
    [0x1b, 0x45, 0x00], // Bold off
  ];
  const cut = settings.autoCut ? [0x1d, 0x56, 0x00] : [];
  return concatBytes(...commands, text, feed, cut);
};

const writeBytes = async (bytes, settings, { requestIfMissing = true } = {}) => {
  const { port, label } = await connectReceiptPrinter(settings, { request: requestIfMissing });
  if (!port.writable) throw new Error("Port printer tidak dapat ditulis");
  const writer = port.writable.getWriter();
  try {
    await writer.write(bytes);
  } finally {
    writer.releaseLock();
  }
  return { label };
};

export const printEscPosReceipt = async ({ sale, outlet, settings, paymentLabel, qrisDetails }, options = {}) => {
  if (!sale) throw new Error("Data transaksi belum tersedia");
  const normalized = normalizePrinterSettings(settings);
  const lines = receiptLines({ sale, outlet, settings: normalized, paymentLabel, qrisDetails });
  return writeBytes(buildEscPosBytes(lines, normalized), normalized, options);
};

export const printEscPosTest = async (settings) => {
  const normalized = normalizePrinterSettings(settings);
  const width = normalized.paperWidth === 80 ? 42 : 32;
  const lines = [
    center("KRAYA", width),
    center("TEST PRINTER ESC/POS", width),
    separator(width),
    columns("Koneksi", "BERHASIL", width),
    columns("Kertas", `${normalized.paperWidth} mm`, width),
    columns("Baud", normalized.baudRate, width),
    separator(width),
    center("RPP02N SIAP DIGUNAKAN", width),
  ];
  return writeBytes(buildEscPosBytes(lines, normalized), normalized, { requestIfMissing: true });
};
